import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { NodeEnvs } from '@finley-technologies/finley-types';

import {
  FeatureFlagSet,
  useFeatureFlags,
} from '~/contexts/FeatureFlagProvider';
import { User, useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

// Exports
/**
 * ⚠️ @eldarshamukhamedov: I can't stress enough how important it is for this context to stay as
 * lean as possible. If you're even contemplating adding other things here (e.g. facilities, CAs,
 * whatever), turn back now. You can implement granular access rules like that in your route
 * business logic; it doesn't belong in global access control.
 *
 * At some point, this context will expose user auth scopes and permission functions will need to be
 * refactored to based access decisions based on those scopes.
 */

export type PermissionContext = {
  flags: FeatureFlagSet;
  user: User;
  env: NodeEnvs;
};
/**
 * ⚠️ @eldarshamukhamedov: Permission functions should not contain any React or async logic.
 */
export type PermissionsFn = (
  context: PermissionContext,
) => 'allow' | 'deny' | 'upsell';

export const usePermissionGuard = (permsFn: PermissionsFn) => {
  const flags = useFeatureFlags();
  const { user } = useUserAuthOrThrow();
  const env = commonUtils.EnvUtils.getEnv();
  return permsFn({ flags, user, env });
};

type Props = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  permissions: PermissionsFn;
  upsell?: React.ReactNode;
};
/**
 * Use `<PermissionGuard />` to evaluate permissions and render children, fallback, or upsell UI,
 * based on the result.
 */
export const PermissionGuard = ({
  children,
  fallback,
  permissions,
  upsell,
}: Props) => {
  const result = usePermissionGuard(permissions);
  if (result === 'upsell') return <>{upsell || fallback || null}</>;
  if (result === 'deny') return <>{fallback || null}</>;
  return <>{children}</>;
};
